import { useState, useCallback } from 'react';
import { CuffaAPI } from 'api/cuffa/cuffa.api';
import { mascaraCpfCnpj } from 'utils/format_cpf';
import { validateCPF } from 'services/utils/validate_cpf';
import { mapCollectionToSituacao, Situacao } from '../utils/status';

const MIN_DELAY_MS = 3000;

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export interface ConfirmData {
  informant_cpf?: string;
  informant_full_name?: string;
  family_unit_name?: string;
  status?: string;
  family_members_count?: number;
  updatedAt?: string;
  resumo?: string;
}

export const useCuffaForm = () => {
  const [cpf, setCpf] = useState('');
  const [cpfValido, setCpfValido] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const [collectionUuid, setCollectionUuid] = useState<string | null>(null);
  const [activeCollection, setActiveCollection] = useState<any>(null);
  const [situacao, setSituacao] = useState<Situacao>('Novo');
  const [openModal, setOpenModal] = useState(false);
  const [confirmData, setConfirmData] = useState<ConfirmData | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [cpfError, setCpfError] = useState<string>('');
  const [cpfInfo, setCpfInfo] = useState<string>('');

  const openConfirm = useCallback((data?: ConfirmData) => {
    setConfirmData(data ?? null);
    setOpenModal(true);
  }, []);

  const closeConfirm = useCallback(() => {
    setOpenModal(false);
  }, []);

  const handleCpfChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const valor = e.target.value.replace(/\D/g, '');
    if (valor.length <= 11) {
      setCpf(valor);
    }
    setCpfValido(null);
    setCpfError('');
    setCpfInfo('');
  }, []);

  const handleVerificarCPF = useCallback(async () => {
    const formattedCpf = mascaraCpfCnpj(cpf);
    const rawCpf = formattedCpf.replace(/[^\d]+/g, '');

    // Validação client-side do CPF
    if (!validateCPF(rawCpf)) {
      setCpfError('CPF inválido. Verifique se o número informado está completo e corresponde a um CPF válido.');
      setCpfValido(false);
      return;
    }

    setCpfError('');
    setCpfInfo('');
    setLoading(true);
    const start = Date.now();

    try {
      const existingRes = await CuffaAPI.checkExistingCpf(formattedCpf);
      const existing = existingRes.dados;

      const elapsed = Date.now() - start;
      if (elapsed < MIN_DELAY_MS) await wait(MIN_DELAY_MS - elapsed);

      if (existing?.has_active_collection && existing?.active_collection_uuid) {
        const activeCollectionUuid = existing.active_collection_uuid;
        const collectionData = await CuffaAPI.getDataByID(activeCollectionUuid);
        setCollectionUuid(activeCollectionUuid);
        setActiveCollection(collectionData?.dados ?? null);
        // set situacao based on collection data
        try {
          const s = mapCollectionToSituacao(collectionData?.dados ?? {});
          setSituacao(s);
        } catch (e) {
          // ignore
        }
        setCpfValido(true);
        return { found: true, activeCollectionUuid, collectionData };
      }

      setCpfInfo('Nenhuma coleta encontrada para o CPF informado. Você pode iniciar uma nova coleta.');
      setCpfValido(true); // CPF is valid, but no collection found
      return { found: false };
    } catch (error) {
      setCpfValido(false);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [cpf]);

  const handleEnviarColeta = useCallback(async (uuid?: string) => {
    setLoading(true);
    try {
      const response = await CuffaAPI.validateAndSend(uuid ?? collectionUuid!);
      setOpenModal(false);
      setActiveCollection((prev: any) => ({ ...prev, editable: false }));
      setSituacao('Enviado ao Estado');
      return response;
    } finally {
      setLoading(false);
    }
  }, [collectionUuid]);

  return {
    cpf,
    setCpf,
    cpfValido,
    loading,
    collectionUuid,
    activeCollection,
    handleCpfChange,
    handleVerificarCPF,
    handleEnviarColeta,
    openModal,
    setOpenModal,
    confirmData,
    setConfirmData,
    confirmLoading,
    setConfirmLoading,
    openConfirm,
    closeConfirm,
    situacao,
    setSituacao,
    cpfError,
    setCpfError,
    cpfInfo,
    setCpfInfo,
  } as const;
};

export default useCuffaForm;
